import React, { useState } from 'react';
import { Row, Col, Form, Alert, Badge, Button, Card } from 'react-bootstrap';
import { FaCheck, FaArrowRight, FaMagic } from 'react-icons/fa';

const SimpleMappingSelection = ({ 
  workflowData, 
  setWorkflowData, 
  sourceClasses, 
  error, 
  loading, 
  gradeLevels 
}) => {
  const [sourceGradeFilter, setSourceGradeFilter] = useState("all");
  const [academicYearFilter, setAcademicYearFilter] = useState("all");
  const [classMappings, setClassMappings] = useState(workflowData.classMappings || {});

  // Academic years for filtering source classes
  const academicYears = [
    { value: "all", label: "All Academic Years" },
    { value: "2027/28", label: "2027/28 Academic Year" },
    { value: "2026/27", label: "2026/27 Academic Year" },
    { value: "2025/26", label: "2025/26 Academic Year" },
    { value: "2024/25", label: "2024/25 Academic Year" },
    { value: "2023/24", label: "2023/24 Academic Year" },
    { value: "2022/23", label: "2022/23 Academic Year" },
    { value: "older", label: "Older School Years" }
  ];

  const getFilteredSourceClasses = () => {
    return sourceClasses.filter(classItem => {
      const matchesGrade = sourceGradeFilter === "all" || classItem.gradeBoolean === sourceGradeFilter;
      const matchesYear = academicYearFilter === "all" || classItem.academicYear === academicYearFilter;
      return matchesGrade && matchesYear;
    });
  };

  // Auto-suggest mappings based on name similarity and grade progression
  const handleAutoMap = () => {
    const newMappings = {};
    const targetClasses = workflowData.createdClasses || [];
    
    sourceClasses.forEach(sourceClass => {
      // Find the best target class match
      const bestMatch = targetClasses.find(targetClass => {
        // First try exact name match (minus grade indicators)
        const sourceBaseName = sourceClass.name.replace(/\b(JI|SI|1st|2nd|3rd|4th|5th|6th)\b/gi, '').trim();
        const targetBaseName = targetClass.name.replace(/\b(JI|SI|1st|2nd|3rd|4th|5th|6th)\b/gi, '').trim();
        
        if (sourceBaseName.toLowerCase() === targetBaseName.toLowerCase()) {
          return true;
        }
        
        // Then try color/name similarity
        const sourceWords = sourceClass.name.toLowerCase().split(' ');
        const targetWords = targetClass.name.toLowerCase().split(' ');
        
        return sourceWords.some(word => targetWords.includes(word) && word.length > 2);
      });
      
      if (bestMatch) {
        newMappings[sourceClass.id] = bestMatch.id;
      }
    });
    
    setClassMappings(newMappings);
    
    // Update workflow data
    setWorkflowData(prev => ({
      ...prev,
      classMappings: newMappings
    }));
  };

  const handleClassMapping = (sourceClassId, targetClassId) => {
    const newMappings = { ...classMappings };
    
    if (targetClassId) {
      newMappings[sourceClassId] = targetClassId;
    } else {
      delete newMappings[sourceClassId];
    }
    
    setClassMappings(newMappings);
    
    // Update workflow data
    setWorkflowData(prev => ({
      ...prev,
      classMappings: newMappings
    }));
  };

  const targetClasses = workflowData.createdClasses || [];
  const filteredSources = getFilteredSourceClasses();
  const mappedCount = Object.keys(classMappings).length;

  return (
    <div>
      <h5>Simple Grade Progression - Class Mapping</h5>
      <p className="text-muted">Map source classes to target classes for 1:1 student transitions.</p>
      
      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="mb-3">
        <Col md={6}>
          <h6>Filter Source Classes</h6>
          <Row>
            <Col md={6}>
              <Form.Select 
                value={sourceGradeFilter} 
                onChange={(e) => setSourceGradeFilter(e.target.value)}
                size="sm"
              >
                {gradeLevels.map(grade => (
                  <option key={grade.value} value={grade.value}>
                    {grade.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Select 
                value={academicYearFilter} 
                onChange={(e) => setAcademicYearFilter(e.target.value)}
                size="sm"
              >
                {academicYears.map(year => (
                  <option key={year.value} value={year.value}>
                    {year.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Col>
        <Col md={6} className="d-flex align-items-end justify-content-end">
          <Button 
            variant="outline-primary" 
            onClick={handleAutoMap}
            disabled={filteredSources.length === 0 || targetClasses.length === 0}
          >
            <FaMagic className="me-2" />
            Auto-Map Classes
          </Button>
        </Col>
      </Row>

      <div className="mb-3">
        <Badge bg="info">
          {mappedCount} of {filteredSources.length} source classes mapped
        </Badge>
      </div>

      <Row className="h-100">
        {/* Left Panel: Source Classes */}
        <Col md={5} className="border-end pe-3">
          <h6 className="mb-3">Source Classes</h6>
          
          <div style={{ height: '450px', overflowY: 'auto' }}>
            {loading ? (
              <div className="text-center py-4">Loading classes...</div>
            ) : filteredSources.length === 0 ? (
              <div className="text-center py-4 text-muted">No source classes found</div>
            ) : (
              <div className="d-grid gap-2">
                {filteredSources.map(sourceClass => (
                  <Card 
                    key={sourceClass.id}
                    className={`${classMappings[sourceClass.id] ? 'border-success' : 'border-light'}`}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-bold">{sourceClass.name}</div>
                          <small className="text-muted">
                            {sourceClass.grade} • {sourceClass.studentCount} students
                            {sourceClass.academicYear !== "older" && ` • ${sourceClass.academicYear}`}
                          </small>
                        </div>
                        {classMappings[sourceClass.id] && (
                          <FaCheck className="text-success" />
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Col>

        {/* Middle: Connection Indicator */}
        <Col md={2} className="d-flex align-items-center justify-content-center">
          <div className="text-center">
            <FaArrowRight size={24} className="text-primary mb-2" />
            <div className="small text-muted">1:1 Mapping</div>
          </div>
        </Col>

        {/* Right Panel: Target Classes */}
        <Col md={5}>
          <h6 className="mb-3">Target Classes (Created)</h6>
          
          <div style={{ height: '450px', overflowY: 'auto' }}>
            {targetClasses.length === 0 ? (
              <div className="text-center py-4 text-muted">No target classes created</div>
            ) : (
              <div className="d-grid gap-2">
                {targetClasses.map(targetClass => {
                  // Find which source class maps to this target
                  const mappedSourceId = Object.keys(classMappings).find(
                    sourceId => classMappings[sourceId] === targetClass.id
                  );
                  const mappedSource = filteredSources.find(sc => sc.id === parseInt(mappedSourceId));
                  
                  return (
                    <Card 
                      key={targetClass.id}
                      className={`${mappedSource ? 'border-success bg-success bg-opacity-10' : 'border-light'}`}
                    >
                      <Card.Body className="p-3">
                        <div className="fw-bold">{targetClass.name}</div>
                        <small className="text-muted">
                          {targetClass.grade_level_number ? gradeLevels.find(g => g.value === targetClass.grade_level_number)?.label : 'No Grade'} • {targetClass.academicYear}
                        </small>
                        
                        {/* Mapping Dropdown */}
                        <Form.Select
                          size="sm"
                          className="mt-2"
                          value={mappedSourceId || ''}
                          onChange={(e) => handleClassMapping(e.target.value, targetClass.id)}
                        >
                          <option value="">Select source class...</option>
                          {filteredSources.map(sourceClass => (
                            <option key={sourceClass.id} value={sourceClass.id}>
                              {sourceClass.name} ({sourceClass.studentCount} students)
                            </option>
                          ))}
                        </Form.Select>
                        
                        {mappedSource && (
                          <div className="mt-2">
                            <small className="text-success">
                              ← {mappedSource.name} ({mappedSource.studentCount} students)
                            </small>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SimpleMappingSelection;